import { useEffect, useState, useRef } from "react";

import styles from "./survey.module.css";
import AppBar from "../../components/appBar/appBar";
import AbortGame from "../../components/abortGame/abortGame";
import PopupWithImage from "../../components/popupWithImage/popupWithImage";
import ImgBuilder from "../../assets/ImgBuilder";
import QuestionBlockSurvey from "../../components/questionBlockSurvey/questionBlockSurvey";

import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useUserData } from '../../functions/updateUserData';
import { useLanguage } from '../../functions/setGetLanguage';
import { setUserInfo, getSurveyQuestions, saveSurveyAnswers, getUserInfo, updateUserProfile} from "../../api/api";

const VALID_SURVEYS = {
  'registration': "/tutorial", 
  'game': "/survey/survey_media", 
  'survey_media': "/finish"
};
const BACK_END_SURVEYS  = ['game', 'survey_media'];

export default function Survey() {
  // recieves what page we are on -> so that it works
  const { type } = useParams(); 
  const [questions, setQuestions] = useState(null);
  const [contentPage, setContentPage] = useState([]);


  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const { userData, setUserData } = useUserData();
  const [answers, setAnswers] = useState({});
  const { language } = useLanguage();
  const [popUpShow, setPopUpShow] = useState(false);
  const [skipPopup, setSkipPopup] = useState(false);
  // 2 useEffects - one runs and checks if I should skip the survey, the other loads data
  // const [checkingSurvey, setCheckingSurvey] = useState(false);
  // const checkingSurveyRef = useRef(false);
  const [userCode, setUserCode] = useState("");
  const loadedTypeRef = useRef("");
  const [isLoaded,setIsLoaded] = useState(false);

  const surveyInitRef = useRef(false);
  const userCheckRef = useRef(false);

  // check if navigation is needed
  useEffect(() => {
    if (userCheckRef.current) return;

    const init = async () => {
      userCheckRef.current = true;
      try {
        const responseUser = await getUserInfo(userData.tokens["access_token"]);
        if (responseUser.media_survey_complete) {
          // skip the survey
          navigate("/finish");
        }
      } catch (error) {
        console.error("Session init failed:", error);
      } finally{
        userCheckRef.current = false;
      }
    };
    if (type === "survey_media") init();
    
  }, [type, navigate, userData.tokens]);

  useEffect(() => {
    if (surveyInitRef.current) return;
    surveyInitRef.current = true;
    if (!Object.keys(VALID_SURVEYS).includes(type)) {
      console.warn(`Invalid survey type: ${type}`);
      navigate("/error");
      return;
    }

    const loadSurvey = async () => {
      if ((BACK_END_SURVEYS.includes(type))) { // if questions are from back-end
        if (loadedTypeRef.current !== type){
          try {
            const key_to_send =  type === "game" ? userData.headlineData.game_survey_id : type;
            const questionsData = await getSurveyQuestions(userData.tokens["access_token"],key_to_send ); // userData.headlineData.trace.pos_user_survey_id
            setQuestions(questionsData.sort((a, b) => a.question_num - b.question_num));
          } catch (error) {
            console.error("Failed to fetch questions:", error);
          } finally{
            loadedTypeRef.current = type;
            surveyInitRef.current = false;
          }
        } else{
          // a case when no type change and hence no API call
          surveyInitRef.current = false;
        }
      } else{
        setQuestions(t((`survey.${type}_questions`), { returnObjects: true } ));
      }
      setContentPage({
        button_text: t((`survey.${type}_button_text`), { returnObjects: true }),
        title: t((`survey.${type}_title`), { returnObjects: true })
      });

      setIsLoaded(true);
    };


    loadSurvey();
  }, [type, language, t, navigate, userData.tokens, userData.headlineData]);

  const validateAnswers = (answers, questions) => {
    // every question has an answer
    const allAnswered = Object.keys(answers).length === questions.length;
    // no empty values
    const noEmptyValues = Object.values(answers).every(value =>
      value !== null &&
      value !== "" &&
      (!Array.isArray(value) || value.length > 0)
    );
    return allAnswered && noEmptyValues;
  };

  const submitAnswers = async () => {
    if (loading) return; // loading is true, no need for additional calls
    if (validateAnswers(answers, questions)){
  
      setLoading(true);
      setUserData(prev => ({
        ...prev,
        [`${type}-survey`]: answers,
      }));   
      // now 2 ways: registration and survey answers
      // 1: registration
      if  (type === "registration"){
        // one must have age given as well, otherwise navigate to age
        if (userData.age_group && (userData.age_group !== undefined)){
          // cause we include age here:
          const userDataToSet = { age_group: userData.age_group, ...answers};
          // currently recieves only sting so stringify ints:
          const userDataToSetString = Object.fromEntries(
            Object.entries(userDataToSet).map(
              ([key, value]) => [
                key,
                Array.isArray(value)
                  ? value.map(v => String(v)) // stringify each element
                  : String(value) // stringify single value
              ]
            )
          );
          try {
            // TODO: if receives ints as well, write userDataToSet instead of userDataToSetSTRING bellow
            // and delete stingify const userDataToSetString above.
            await setUserInfo(userData.tokens.access_token, userDataToSetString); 
            setUserCode(userData.tokens.user_code);
          } catch (err) {
            console.error("Error: ",err);
          } finally{
            setLoading(false);
          }
        }
        else{
          navigate('/age');
        }
      }
      // 2: survey answers
      else{
        // as it recieves only arrays -> I send only arrays, so I make all ints a 1-long array
        // TODO: if /survey/pos-op/save accepts int, delete this part as it is redundancy
        const userDataToSetArray = Object.fromEntries(
          Object.entries(answers).map(
            ([key, value]) => [key, Array.isArray(value) ? value : [value]]
          )
        );
        try {
          const surveyId = (type==="game") ? userData.headlineData.game_survey_id : type;

          await saveSurveyAnswers(userData.tokens.access_token,userData.headlineData.trace_id,surveyId,userDataToSetArray);

          if (type==="survey_media"){
            await updateUserProfile(userData.tokens.access_token, {"media_survey_complete": true});
          } else if (type==="game"){
            setUserData(prev => ({
              ...prev,
              finishedSurveyGame: true,
            }));   
          }
          navigate((userData?.media_survey_complete && (type==="game")) ? "/finish" : VALID_SURVEYS[type]);
        } catch (err) {
          console.error("Error: ",err);
          navigate('/finish')
        } finally{
          setLoading(false);
          window.scrollTo(0, 0);
          setIsLoaded(false);
          setAnswers({});
        }
      }
    } else{
      setPopUpShow(true);
    }
  };


  const handleAnswerChange = (questionKey, selectedValue, questionType) => {
    if (questionType === "single_choice") {
      setAnswers(prev => ({
        ...prev,
        [questionKey]: prev[questionKey] === selectedValue ? null : selectedValue
      }));
    } else if (questionType === "multiple_choice"){
      setAnswers(prev => {
        // if empty -> creates a list and pushes answers there
        const currentArray = Array.isArray(prev[questionKey]) ? prev[questionKey] : [];
        // if selected -> disselect
        const isAlreadySelected = currentArray.includes(selectedValue);
        const updatedArray = isAlreadySelected
          ? currentArray.filter(v => v !== selectedValue)
          : [...currentArray, selectedValue];

        return {
          ...prev,
          [questionKey]: updatedArray
        };
      });
    } else {
      return;
    }
  };

  return (
    <div 
      className={styles.wrapper}
      // style={{
      //   backgroundImage: `url(${ImgBuilder.StirringEmotion})`,
      //   backgroundRepeat: "repeat-y",
      // }}
    >
      <AppBar />
      <AbortGame />

      {/* TODO: Currently a lazy 3 separate pop-ups, I think I need to create a function that creates it*/}
      {popUpShow &&
        <PopupWithImage
          name={t(`pop_up_notifications.miss_fields`)}
          button={t(`pop_up_notifications.miss_fields_conf`)}
          type={"confirmation"}
          defContinue={() => setPopUpShow(false)}
        />    
      }
      {skipPopup &&
        <PopupWithImage
          name={t(`pop_up_notifications.name_survey_skip`)}
          button={ t(`pop_up_notifications.button_exit_survey_skip`)}
          description={t(`pop_up_notifications.description_survey_skip`)}
          button_exit={t(`pop_up_notifications.button_survey_skip`)}
          type={"confirmation"}
          defContinue={() => navigate(VALID_SURVEYS[type])}
          defExit={() =>  setSkipPopup(false)}
        />    
      }
      {userCode &&
        <PopupWithImage
          name={t("pop_up_notifications.name_login")}
          description={t("pop_up_notifications.description_login")}
          button={t("pop_up_notifications.button_login")}
          type={"showId"}
          content={userCode}
          defContinue={() => navigate("/tutorial")}
        />
      }

      <div className={styles.container}>

        <p 
          className={styles.pageName} 
          style={{
            opacity: isLoaded ? 1 : 0,
            transition: "opacity 0.5s ease-in-out",
          }}
        >
          {contentPage.title}
        </p>

        <div className={styles.containerBlocks}>
          {!isLoaded &&
            Array.from({ length: 9 }).map((_, i) => (
              <div key={i} className={styles.loadingBox}>
                <div className={styles.leftLoadingBox}></div>
                <div className={styles.rightLoadingBox}>
                  <div className={styles.nameRightLoadingBox}></div>
                  <div className={styles.contentRightLoadingBox}></div>
                </div>
              </div>
            ))
          }
          {(Array.isArray(questions) && questions.length !== 0)  &&
          questions.map((value, index) => (
            <QuestionBlockSurvey 
              style={{
                opacity: isLoaded ? 1 : 0,
                transition: "opacity 0.5s ease-in-out",
              }}
              key={index} 
              value={value} 
              index={index}
              typeQuestion={value}
              selected={
                value.question_type === "multiple_choice"
                  ? answers[value.question_num] || []
                  : answers[value.question_num] ?? null
              }
              onChange={selected => handleAnswerChange(value.question_num, selected, value.question_type)}
            />
          ))}

        </div>
      </div>

      <div className={styles.buttonsContainer}>
        <button onClick={() => submitAnswers()} className={styles.button}> {contentPage.button_text}</button>
        {type==="survey_media" &&
          <button onClick={() => setSkipPopup(true)} className={`${styles.button} ${styles.buttonSkip}`}>{t(`survey.button_skip`)}</button>
        } 
      </div>

      <div 
        className={styles.bgDiv}
        style={{
          backgroundImage: `url(${ImgBuilder.StirringEmotion})`
        }}
      >

      </div>
    </div>
  );
}
